import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Divider
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { SketchPicker } from 'react-color';
import { BankAccount } from '../types/BankAccount';
import { designTokens } from '../theme/designTokens';

interface BankAccountFormProps {
    open: boolean;
    onClose: () => void;
    onSave: (account: Omit<BankAccount, 'id'> | BankAccount) => Promise<void>;
    initialData?: BankAccount;
}

const BankAccountForm: React.FC<BankAccountFormProps> = ({ open, onClose, onSave, initialData }) => {
    const intl = useIntl();
    const [name, setName] = useState('');
    const [type, setType] = useState<'checking' | 'savings' | 'investment' | 'cash' | 'other'>('checking');
    const [balance, setBalance] = useState('');
    const [color, setColor] = useState(designTokens.colors.info.main);
    const [showColorPicker, setShowColorPicker] = useState(false);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name);
            setType(initialData.type || 'checking');
            setBalance(initialData.balance.toString());
            setColor(initialData.color);
        } else {
            setName('');
            setType('checking');
            setBalance('');
            setColor(designTokens.colors.info.main);
        }
    }, [initialData, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const accountData = {
            name,
            type,
            balance: isNaN(parseFloat(balance)) ? 0 : parseFloat(balance),
            color,
            ...(initialData && initialData.id && { id: initialData.id }),
            ...(initialData && initialData.userId && { userId: initialData.userId }),
            ...(initialData && initialData.createdAt && { createdAt: initialData.createdAt }),
            ...(initialData && initialData.updatedAt && { updatedAt: initialData.updatedAt }),
        };
        onSave(accountData as Omit<BankAccount, 'id'> | BankAccount);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: designTokens.borderRadius.lg,
                    background: designTokens.colors.background.paper,
                    border: designTokens.glassmorphism.border,
                    boxShadow: designTokens.shadows.lg
                }
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold, pb: 1 }}>
                    <FormattedMessage id={initialData ? "editBankAccount" : "addBankAccount"} defaultMessage={initialData ? "Edit Bank Account" : "Add Bank Account"} />
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            autoFocus
                            label={<FormattedMessage id="name" defaultMessage="Name" />}
                            type="text"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: designTokens.borderRadius.md } }}
                        />
                        <FormControl fullWidth>
                            <InputLabel><FormattedMessage id="type" defaultMessage="Type" /></InputLabel>
                            <Select
                                value={type}
                                label={<FormattedMessage id="type" defaultMessage="Type" />}
                                onChange={(e) => setType(e.target.value as 'checking' | 'savings' | 'investment' | 'cash' | 'other')}
                                sx={{ borderRadius: designTokens.borderRadius.md }}
                            >
                                <MenuItem value="checking"><FormattedMessage id="accountType.checking" defaultMessage="Checking" /></MenuItem>
                                <MenuItem value="savings"><FormattedMessage id="accountType.savings" defaultMessage="Savings" /></MenuItem>
                                <MenuItem value="investment"><FormattedMessage id="accountType.investment" defaultMessage="Investment" /></MenuItem>
                                <MenuItem value="cash"><FormattedMessage id="accountType.cash" defaultMessage="Cash" /></MenuItem>
                                <MenuItem value="other"><FormattedMessage id="accountType.other" defaultMessage="Other" /></MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label={<FormattedMessage id="balance" defaultMessage="Balance" />}
                            type="number"
                            fullWidth
                            value={balance}
                            onChange={(e) => setBalance(e.target.value)}
                            required
                            inputProps={{ step: "0.01" }}
                            variant="outlined"
                            InputProps={{ sx: { borderRadius: designTokens.borderRadius.md } }}
                        />
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: designTokens.colors.text.secondary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                <FormattedMessage id="color" defaultMessage="Color" />
                            </Typography>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 44,
                                    backgroundColor: color,
                                    borderRadius: designTokens.borderRadius.md,
                                    cursor: 'pointer',
                                    border: `1px solid ${designTokens.colors.background.elevated}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': { transform: 'scale(1.01)' }
                                }}
                                onClick={() => setShowColorPicker(!showColorPicker)}
                            >
                                <Typography variant="caption" sx={{ color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)', fontWeight: 'bold' }}>
                                    {color?.toUpperCase() || ''}
                                </Typography>
                            </Box>
                            {showColorPicker && (
                                <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
                                    <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }} onClick={() => setShowColorPicker(false)} />
                                    <SketchPicker
                                        color={color}
                                        onChangeComplete={(c) => setColor(c.hex)}
                                        disableAlpha
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <Divider sx={{ mx: 2, opacity: 0.5 }} />
                <DialogActions sx={{ p: 2, gap: 1.5 }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            textTransform: 'none',
                            borderRadius: designTokens.borderRadius.md,
                            color: designTokens.colors.text.secondary
                        }}
                    >
                        <FormattedMessage id="cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            borderRadius: designTokens.borderRadius.md,
                            px: 4,
                            background: designTokens.colors.primary.gradient,
                            boxShadow: designTokens.shadows.md
                        }}
                    >
                        <FormattedMessage id="save" defaultMessage="Save" />
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BankAccountForm;
